/**
 * <ReportScreenModal /> — Modal global de "Reportar problema nesta tela".
 *
 * Spec: CHAMADOS_VISIBILIDADE_SPEC §9.3.5 + §19.2 G-N7 (Tenant Select fallback).
 *
 * Comportamento:
 *  - Controlado por useReportModalStore (Zustand): opened + prefilledError
 *  - Coleta contexto da tela via useScreenContext
 *  - Submete via useReportScreen (mutation -> POST /v1/chamados/from-screen)
 *  - Pre-preenche titulo com route_label (+ message do prefilledError, se houver)
 *  - Default priority: 'high' se prefilledError presente, 'medium' senao
 *  - Tenant Select (G-N7): so renderizado se user tem chamados:request em >1 tenant
 *  - Screenshot: dynamic import de html2canvas + ignoreElements data-sensitive
 *  - Mobile: fullScreen via useMediaQuery (max-width: 768px)
 */

import { useEffect, useMemo, useState } from 'react'
import {
  Modal,
  Stack,
  TextInput,
  Textarea,
  Select,
  Button,
  Group,
  Image,
  Alert,
  Text,
  ScrollArea,
} from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import {
  IconUpload,
  IconAlertCircle,
  IconCheck,
} from '@tabler/icons-react'
import { useReportModalStore } from '@/store/useReportModalStore'
import { useScreenContext } from '@/hooks/useScreenContext'
import { useReportScreen } from '@/hooks/useReportScreen'
import { useAuthStore } from '@/store/auth'
import { formatApiError } from '@/api/errors'
import { ReportScreenContextPanel } from './ReportScreenContextPanel'

const PRIORITIES = [
  { value: 'low', label: 'Baixa' },
  { value: 'medium', label: 'Média' },
  { value: 'high', label: 'Alta' },
  { value: 'critical', label: 'Critica' },
]

type Priority = 'low' | 'medium' | 'high' | 'critical'

export function ReportScreenModal() {
  const isMobile = useMediaQuery('(max-width: 768px)')
  const opened = useReportModalStore((s) => s.opened)
  const close = useReportModalStore((s) => s.close)
  const prefilledError = useReportModalStore((s) => s.prefilledError)
  const screenContext = useScreenContext()
  const reportScreen = useReportScreen()
  const { mutate, isPending, isSuccess, isError, error } = reportScreen

  const user = useAuthStore((s) => s.user)
  const selectedTenantId = useAuthStore((s) => s.selectedTenantId)

  // Determinar tenants candidatos: aqueles em que o user tem chamados:request.
  // Inclui admin:full e chamados:request via globals tambem.
  const candidateTenants = useMemo(() => {
    if (!user) return [] as Array<{ value: string; label: string }>
    const globals = user.global_permissions ?? []
    const isSuper = globals.includes('admin:full') || globals.includes('chamados:request')
    const tenantPerms = user.tenant_permissions ?? {}
    const tenants = user.tenants ?? []
    return tenants
      .filter((t) => {
        if (isSuper) return true
        const perms = tenantPerms[String(t.id)] ?? []
        return perms.includes('chamados:request')
      })
      .map((t) => ({ value: String(t.id), label: t.name }))
  }, [user])

  const showTenantSelect = candidateTenants.length > 1

  // Form state
  const [tenantId, setTenantId] = useState<string | null>(null)
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [priority, setPriority] = useState<Priority>('medium')
  const [screenshotBase64, setScreenshotBase64] = useState<string | null>(null)
  const [capturing, setCapturing] = useState(false)

  // Pre-preenchimento ao abrir o modal
  useEffect(() => {
    if (!opened) return

    // Tenant default: selectedTenantId se aplicavel; senao primeiro candidato
    const defaultTenant =
      selectedTenantId &&
      candidateTenants.some((t) => t.value === String(selectedTenantId))
        ? String(selectedTenantId)
        : candidateTenants[0]?.value ?? null
    setTenantId(defaultTenant)

    // Title default
    const baseTitle = `Problema em "${screenContext.route_label}"`
    setTitle(
      prefilledError?.message
        ? `${baseTitle}: ${prefilledError.message.slice(0, 80)}`
        : baseTitle,
    )

    // Description default — anexa stack se houver
    setDescription(
      prefilledError?.componentStack
        ? `Stack:\n${prefilledError.componentStack}`
        : '',
    )

    // Priority default: high se erro pre-preenchido (G-N7), medium senao
    setPriority(prefilledError ? 'high' : 'medium')

    // Reset screenshot
    setScreenshotBase64(null)
  }, [
    opened,
    prefilledError,
    candidateTenants,
    selectedTenantId,
    screenContext.route_label,
  ])

  const handleCaptureScreenshot = async () => {
    setCapturing(true)
    // Esconde o proprio modal (e qualquer overlay/portal Mantine) durante a
    // captura, para que o screenshot mostre a tela de fundo onde o erro ocorreu
    // — e nao o modal "Reportar problema nesta tela" sobreposto a ela.
    const hiddenEls: Array<{ el: HTMLElement; prev: string }> = []
    const selector =
      '.mantine-Modal-root, .mantine-Drawer-root, .mantine-Overlay-root, .mantine-Notifications-root'
    document.querySelectorAll<HTMLElement>(selector).forEach((el) => {
      hiddenEls.push({ el, prev: el.style.visibility })
      el.style.visibility = 'hidden'
    })
    // Aguarda dois rAFs para garantir que o browser pintou o frame sem o modal
    await new Promise<void>((resolve) =>
      requestAnimationFrame(() => requestAnimationFrame(() => resolve())),
    )
    try {
      const html2canvas = (await import('html2canvas')).default
      const canvas = await html2canvas(document.body, {
        ignoreElements: (el) => {
          if (el.getAttribute('data-sensitive') === 'true') return true
          // Defesa extra: ignora qualquer portal/overlay do Mantine que
          // tenha escapado do visibility:hidden acima.
          if (
            typeof (el as HTMLElement).closest === 'function' &&
            (el as HTMLElement).closest(selector)
          ) {
            return true
          }
          return false
        },
        scale: 0.5,
      })
      const dataUrl = canvas.toDataURL('image/png')
      // Remove o prefixo "data:image/png;base64," — backend espera apenas base64
      const base64 = dataUrl.split(',')[1]
      setScreenshotBase64(base64 ?? null)
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Falha ao capturar screenshot', err)
    } finally {
      hiddenEls.forEach(({ el, prev }) => {
        el.style.visibility = prev
      })
      setCapturing(false)
    }
  }

  const handleSubmit = () => {
    if (!tenantId) return
    mutate({
      tenant_id: Number(tenantId),
      title: title.trim() || undefined,
      description: description.trim() || undefined,
      priority,
      screen_origin: screenContext,
      screenshot_base64: screenshotBase64 ?? undefined,
    })
  }

  // Edge case: nenhum tenant disponivel para abrir chamado
  if (opened && candidateTenants.length === 0) {
    return (
      <Modal
        opened={opened}
        onClose={close}
        title="Reportar problema"
        size="md"
        closeButtonProps={{ 'aria-label': 'Fechar' }}
      >
        <Alert icon={<IconAlertCircle size={16} />} color="orange">
          Você não tem permissão de criar chamados em nenhum tenant. Contate o
          administrador.
        </Alert>
      </Modal>
    )
  }

  const errorMessage = formatApiError(error, 'Erro ao criar chamado')

  return (
    <Modal
      opened={opened}
      onClose={close}
      title="Reportar problema nesta tela"
      size="lg"
      fullScreen={isMobile}
      centered={!isMobile}
      trapFocus
      closeOnEscape
      closeButtonProps={{ 'aria-label': 'Fechar' }}
    >
      <ScrollArea h={isMobile ? '80vh' : '70vh'}>
        <Stack gap="sm">
          {isSuccess && (
            <Alert icon={<IconCheck size={16} />} color="green">
              Chamado criado com sucesso. Você pode fechar esta janela.
            </Alert>
          )}
          {isError && (
            <Alert icon={<IconAlertCircle size={16} />} color="red">
              {errorMessage}
            </Alert>
          )}

          {showTenantSelect && (
            <Select
              label="Tenant"
              required
              data={candidateTenants}
              value={tenantId}
              onChange={setTenantId}
              description="Onde abrir este chamado"
            />
          )}

          <Select
            label="Prioridade"
            required
            data={PRIORITIES}
            value={priority}
            onChange={(v) => v && setPriority(v as Priority)}
          />

          <TextInput
            label="Título"
            required
            value={title}
            onChange={(e) => setTitle(e.currentTarget.value)}
            autoFocus
          />

          <Textarea
            label="Descrição (opcional)"
            placeholder="O que você estava tentando fazer? O que aconteceu?"
            value={description}
            onChange={(e) => setDescription(e.currentTarget.value)}
            minRows={4}
            autosize
          />

          <Stack gap={4}>
            <Text size="sm" fw={500}>
              Screenshot (opcional)
            </Text>
            {screenshotBase64 ? (
              <Stack gap={4}>
                <Image
                  src={`data:image/png;base64,${screenshotBase64}`}
                  alt="Screenshot"
                  fit="contain"
                  mah={200}
                />
                <Button
                  variant="subtle"
                  size="xs"
                  color="red"
                  onClick={() => setScreenshotBase64(null)}
                >
                  Remover screenshot
                </Button>
              </Stack>
            ) : (
              <Button
                variant="default"
                leftSection={<IconUpload size={16} />}
                onClick={handleCaptureScreenshot}
                loading={capturing}
                size="sm"
              >
                Capturar tela atual
              </Button>
            )}
            <Text size="xs" c="dimmed">
              Campos com `data-sensitive` são automaticamente ocultados.
            </Text>
          </Stack>

          <ReportScreenContextPanel context={screenContext} />

          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={close} disabled={isPending}>
              Cancelar
            </Button>
            <Button
              onClick={handleSubmit}
              loading={isPending}
              disabled={!title.trim() || !tenantId || isSuccess}
            >
              Abrir chamado
            </Button>
          </Group>
        </Stack>
      </ScrollArea>
    </Modal>
  )
}
